import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

interface CountdownTimerProps {
  deadline: string | null | undefined;
  label?: string;
  compact?: boolean;
}

export function CountdownTimer({ deadline, label = 'Submission Deadline', compact = false }: CountdownTimerProps) {
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    expired: boolean;
  } | null>(null);

  useEffect(() => {
    if (!deadline) {
      setTimeRemaining(null);
      return;
    }

    const calculateTimeRemaining = () => {
      const deadlineDate = new Date(deadline);
      const now = new Date();
      
      // Set deadline to end of day (23:59:59)
      deadlineDate.setHours(23, 59, 59, 999);
      
      const diff = deadlineDate.getTime() - now.getTime();
      
      if (diff <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          expired: true,
        };
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return {
        days,
        hours,
        minutes,
        seconds,
        expired: false,
      };
    };

    // Calculate immediately
    setTimeRemaining(calculateTimeRemaining());

    // Update every second
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);

    return () => clearInterval(interval);
  }, [deadline]);

  if (!deadline || !timeRemaining) {
    return null;
  }

  if (timeRemaining.expired) {
    if (compact) {
      return (
        <div className="bg-red-50 border border-red-200 rounded px-2 py-1">
          <div className="text-xs text-red-700 font-medium">Expired</div>
        </div>
      );
    }
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-900 font-semibold">
          <Clock className="h-5 w-5" />
          <span>Submission Deadline Passed</span>
        </div>
        <div className="text-sm text-red-700 mt-1">
          The deadline was {new Date(deadline).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>
      </div>
    );
  }

  const isUrgent = timeRemaining.days === 0 && timeRemaining.hours < 24;
  const isVeryUrgent = timeRemaining.days === 0 && timeRemaining.hours < 6;

  if (compact) {
    return (
      <div className={`border rounded px-2.5 py-1.5 ${
        isVeryUrgent 
          ? 'bg-red-50 border-red-200' 
          : isUrgent 
          ? 'bg-yellow-50 border-yellow-200' 
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className={`flex items-center gap-1.5 text-xs font-medium ${
          isVeryUrgent 
            ? 'text-red-600' 
            : isUrgent 
            ? 'text-yellow-600' 
            : 'text-blue-600'
        }`}>
          <Clock className={`h-3.5 w-3.5 ${
            isVeryUrgent 
              ? 'text-red-500' 
              : isUrgent 
              ? 'text-yellow-500' 
              : 'text-blue-500'
          }`} />
          <span className="tabular-nums">
            {timeRemaining.days > 0 && `${timeRemaining.days}d `}
            {String(timeRemaining.hours).padStart(2, '0')}:{String(timeRemaining.minutes).padStart(2, '0')}:{String(timeRemaining.seconds).padStart(2, '0')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`border rounded-lg p-4 ${
      isVeryUrgent 
        ? 'bg-red-50 border-red-200' 
        : isUrgent 
        ? 'bg-yellow-50 border-yellow-200' 
        : 'bg-blue-50 border-blue-200'
    }`}>
      <div className="flex items-center gap-2 mb-3">
        <Clock className={`h-5 w-5 ${
          isVeryUrgent 
            ? 'text-red-600' 
            : isUrgent 
            ? 'text-yellow-600' 
            : 'text-blue-600'
        }`} />
        <span className={`font-semibold ${
          isVeryUrgent 
            ? 'text-red-900' 
            : isUrgent 
            ? 'text-yellow-900' 
            : 'text-blue-900'
        }`}>
          {label}
        </span>
      </div>
      
      <div className="flex items-center gap-4">
        {timeRemaining.days > 0 && (
          <div className="text-center">
            <div className={`text-2xl font-bold ${
              isVeryUrgent 
                ? 'text-red-600' 
                : isUrgent 
                ? 'text-yellow-600' 
                : 'text-blue-600'
            }`}>
              {timeRemaining.days}
            </div>
            <div className={`text-xs uppercase tracking-wide ${
              isVeryUrgent 
                ? 'text-red-700' 
                : isUrgent 
                ? 'text-yellow-700' 
                : 'text-blue-700'
            }`}>
              {timeRemaining.days === 1 ? 'Day' : 'Days'}
            </div>
          </div>
        )}
        <div className="text-center">
          <div className={`text-2xl font-bold ${
            isVeryUrgent 
              ? 'text-red-600' 
              : isUrgent 
              ? 'text-yellow-600' 
              : 'text-blue-600'
          }`}>
            {String(timeRemaining.hours).padStart(2, '0')}
          </div>
          <div className={`text-xs uppercase tracking-wide ${
            isVeryUrgent 
              ? 'text-red-700' 
              : isUrgent 
              ? 'text-yellow-700' 
              : 'text-blue-700'
          }`}>
            {timeRemaining.hours === 1 ? 'Hour' : 'Hours'}
          </div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-bold ${
            isVeryUrgent 
              ? 'text-red-600' 
              : isUrgent 
              ? 'text-yellow-600' 
              : 'text-blue-600'
          }`}>
            {String(timeRemaining.minutes).padStart(2, '0')}
          </div>
          <div className={`text-xs uppercase tracking-wide ${
            isVeryUrgent 
              ? 'text-red-700' 
              : isUrgent 
              ? 'text-yellow-700' 
              : 'text-blue-700'
          }`}>
            {timeRemaining.minutes === 1 ? 'Min' : 'Mins'}
          </div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-bold ${
            isVeryUrgent 
              ? 'text-red-600' 
              : isUrgent 
              ? 'text-yellow-600' 
              : 'text-blue-600'
          }`}>
            {String(timeRemaining.seconds).padStart(2, '0')}
          </div>
          <div className={`text-xs uppercase tracking-wide ${
            isVeryUrgent 
              ? 'text-red-700' 
              : isUrgent 
              ? 'text-yellow-700' 
              : 'text-blue-700'
          }`}>
            {timeRemaining.seconds === 1 ? 'Sec' : 'Secs'}
          </div>
        </div>
      </div>
      
      <div className={`text-xs mt-3 ${
        isVeryUrgent 
          ? 'text-red-700' 
          : isUrgent 
          ? 'text-yellow-700' 
          : 'text-blue-700'
      }`}>
        Deadline: {new Date(deadline).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </div>
    </div>
  );
}

